import { useState, useEffect } from 'react';

interface AttendanceStatus {
  id: string;
  user_id: string;
  clock_in: string;
  clock_out: string | null;
  total_hours: number | null;
  status: 'active' | 'break' | 'finished';
  created_at: string;
}

export function useClockIn() {
  const [status, setStatus] = useState<AttendanceStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [duration, setDuration] = useState('00:00:00');

  // Carregar status atual
  const loadStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/attendance/my-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.success) {
        setStatus(data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar status:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fazer clock-in
  const clockIn = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/attendance/clock-in`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();
      
      if (data.success) {
        setStatus(data.data);
        return { success: true };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('Erro ao fazer clock-in:', error);
      return { success: false, error: 'Erro ao fazer clock-in' };
    }
  };

  // Fazer clock-out
  const clockOut = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/attendance/clock-out`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();
      
      if (data.success) {
        setStatus(null);
        return { success: true };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('Erro ao fazer clock-out:', error);
      return { success: false, error: 'Erro ao fazer clock-out' };
    }
  };

  // Iniciar pausa
  const startBreak = async (reason?: string) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/attendance/break`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason })
      });
      const data = await res.json();
      
      if (data.success) {
        await loadStatus();
        return { success: true };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('Erro ao iniciar pausa:', error);
      return { success: false, error: 'Erro ao iniciar pausa' };
    }
  };

  // Retomar do intervalo
  const resumeWork = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/attendance/resume`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();
      
      if (data.success) {
        await loadStatus();
        return { success: true };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('Erro ao retomar trabalho:', error);
      return { success: false, error: 'Erro ao retomar trabalho' };
    }
  };

  // Calcular duração em tempo real
  useEffect(() => {
    if (!status || !status.clock_in) return;

    const interval = setInterval(() => {
      // Converter para horário local do navegador
      const start = new Date(status.clock_in + 'Z'); // Adiciona Z para indicar UTC
      const now = new Date();
      const diff = now.getTime() - start.getTime();

      // Se diff for negativo, significa problema de timezone
      if (diff < 0) {
        setDuration('00:00:00');
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setDuration(
        `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
      );
    }, 1000);

    return () => clearInterval(interval);
  }, [status]);

  // Carregar status ao montar
  useEffect(() => {
    loadStatus();
  }, []);

  return {
    status,
    loading,
    duration,
    clockIn,
    clockOut,
    startBreak,
    resumeWork,
    reload: loadStatus
  };
}
